import type {
  AnswerValue,
  AssetLinkValue,
  CurrencyValue,
  FieldDef,
  FileRefValue,
  RepeaterValue,
  SelectValue,
  SignatureValue,
  WeeklyHoursValue,
} from './types.js';
import { WEEK_DAYS } from './types.js';

export const DEFAULT_LIMITS = {
  shortTextMax: 200,
  longTextMax: 5000,
  /** Safe bounds tenants may configure within. */
  shortTextHardMax: 500,
  longTextHardMax: 20000,
  repeaterHardMaxRows: 200,
} as const;

export function isBlankAnswer(a: AnswerValue | undefined | null): boolean {
  if (!a || a.kind === 'blank') return true;
  if (a.kind !== 'value') return false;
  const v = a.value as unknown;
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (typeof v === 'number' || typeof v === 'boolean') return false; // 0 and false are real values
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if ('selected' in o) return !Array.isArray(o.selected) || o.selected.length === 0;
    if ('rows' in o) return !Array.isArray(o.rows) || (o.rows as unknown[]).length === 0;
    if ('assetIds' in o) return !Array.isArray(o.assetIds) || o.assetIds.length === 0;
    if ('url' in o) return typeof o.url !== 'string' || o.url.trim() === '';
    if ('name' in o && Object.keys(o).every((k) => k === 'name' || k === 'signedAt'))
      return typeof o.name !== 'string' || o.name.trim() === '';
    if ('amount' in o) return typeof o.amount !== 'number';
    if ('days' in o) return false;
    return Object.keys(o).length === 0;
  }
  return false;
}

export interface ValueIssue {
  code: string;
  message: string;
}

const EMAIL_RE = /^[^\s@]{1,64}@[^\s@]{1,190}\.[^\s@]{2,}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function normalizePhone(input: string): string | null {
  const trimmed = input.trim();
  const plus = trimmed.startsWith('+');
  const digits = trimmed.replace(/[^\d]/g, '');
  if (digits.length < 7 || digits.length > 15) return null;
  return (plus ? '+' : '') + digits;
}

export function isSafeHttpUrl(input: string): boolean {
  try {
    const u = new URL(input.trim());
    return (u.protocol === 'http:' || u.protocol === 'https:') && !!u.hostname && !u.username && !u.password;
  } catch {
    return false;
  }
}

function isValidDate(s: string): boolean {
  if (!DATE_RE.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

function toMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number) as [number, number];
  return h * 60 + m;
}

export function validateWeeklyHours(v: WeeklyHoursValue): ValueIssue[] {
  const issues: ValueIssue[] = [];
  if (!v || typeof v !== 'object' || !v.days) return [{ code: 'hours.invalid', message: 'Hours are invalid.' }];
  if (!v.timezone || typeof v.timezone !== 'string') issues.push({ code: 'hours.timezone', message: 'Timezone is required.' });
  const checkIntervals = (label: string, closed: boolean, intervals: { open: string; close: string }[]) => {
    if (closed) return; // closed days never require times
    if (!Array.isArray(intervals) || intervals.length === 0) {
      issues.push({ code: 'hours.missing', message: `${label}: add opening hours or mark closed.` });
      return;
    }
    const spans: Array<[number, number]> = [];
    for (const i of intervals) {
      if (!TIME_RE.test(i.open) || !TIME_RE.test(i.close)) {
        issues.push({ code: 'hours.format', message: `${label}: times must be HH:mm.` });
        continue;
      }
      const o = toMinutes(i.open);
      let c = toMinutes(i.close);
      if (o === c) {
        issues.push({ code: 'hours.zero', message: `${label}: opening and closing time cannot be equal.` });
        continue;
      }
      if (c < o) c += 24 * 60; // overnight
      spans.push([o, c]);
    }
    spans.sort((a, b) => a[0] - b[0]);
    for (let i = 1; i < spans.length; i++) {
      if (spans[i]![0] < spans[i - 1]![1]) {
        issues.push({ code: 'hours.overlap', message: `${label}: intervals overlap.` });
        break;
      }
    }
  };
  for (const d of WEEK_DAYS) {
    const day = v.days[d];
    if (!day) {
      issues.push({ code: 'hours.day_missing', message: `${d}: missing.` });
      continue;
    }
    checkIntervals(d.toUpperCase(), day.closed, day.intervals);
  }
  const seen = new Set<string>();
  for (const o of v.overrides ?? []) {
    if (!isValidDate(o.date)) issues.push({ code: 'hours.override_date', message: `Holiday override date is invalid.` });
    if (seen.has(o.date)) issues.push({ code: 'hours.override_dup', message: `Duplicate override for ${o.date}.` });
    seen.add(o.date);
    checkIntervals(o.label || o.date, o.closed, o.intervals);
  }
  return issues;
}

/**
 * Validates a non-blank answer's shape and constraints. Blank/required handling is
 * done by progress.ts; this function only validates provided content.
 */
export function validateAnswer(field: FieldDef, a: AnswerValue | undefined): ValueIssue[] {
  if (!a || a.kind === 'blank') return [];
  if (a.kind === 'unknown') {
    return field.allowUnknown ? [] : [{ code: 'unknown.not_allowed', message: '“Unknown” is not allowed for this field.' }];
  }
  if (a.kind === 'na') {
    return field.allowNa ? [] : [{ code: 'na.not_allowed', message: '“Not applicable” is not allowed for this field.' }];
  }
  if (a.kind === 'waived') return [];
  const v = a.value as unknown;
  const val = field.validation ?? {};
  const issues: ValueIssue[] = [];
  const str = (max: number) => {
    if (typeof v !== 'string') return issues.push({ code: 'type', message: 'Must be text.' });
    const len = v.trim().length;
    // Callers pass the effective limit (template maxLength capped by the hard maximum).
    const limit = max;
    if (len > limit) issues.push({ code: 'too_long', message: `Must be ${limit} characters or fewer.` });
    if (val.minLength && len < val.minLength) issues.push({ code: 'too_short', message: `Must be at least ${val.minLength} characters.` });
    return 0;
  };
  switch (field.type) {
    case 'short_text':
      str(Math.min(val.maxLength ?? DEFAULT_LIMITS.shortTextMax, DEFAULT_LIMITS.shortTextHardMax));
      break;
    case 'long_text':
      str(Math.min(val.maxLength ?? DEFAULT_LIMITS.longTextMax, DEFAULT_LIMITS.longTextHardMax));
      break;
    case 'email':
      if (typeof v !== 'string' || !EMAIL_RE.test(v.trim())) issues.push({ code: 'email', message: 'Enter a valid email address.' });
      break;
    case 'phone':
      if (typeof v !== 'string' || !normalizePhone(v)) issues.push({ code: 'phone', message: 'Enter a valid phone number (7–15 digits).' });
      break;
    case 'url':
      if (typeof v !== 'string' || !isSafeHttpUrl(v)) issues.push({ code: 'url', message: 'Enter a valid http(s) URL.' });
      break;
    case 'integer':
    case 'decimal':
    case 'year': {
      if (typeof v !== 'number' || !Number.isFinite(v)) {
        issues.push({ code: 'number', message: 'Enter a number.' });
        break;
      }
      if ((field.type === 'integer' || field.type === 'year') && !Number.isInteger(v))
        issues.push({ code: 'integer', message: 'Enter a whole number.' });
      if (field.type === 'year') {
        const max = new Date().getUTCFullYear() + 1;
        if (v < 1800 || v > max) issues.push({ code: 'year', message: `Enter a year between 1800 and ${max}.` });
      }
      if (val.min !== undefined && v < val.min) issues.push({ code: 'min', message: `Must be at least ${val.min}.` });
      if (val.max !== undefined && v > val.max) issues.push({ code: 'max', message: `Must be at most ${val.max}.` });
      break;
    }
    case 'currency': {
      const c = v as CurrencyValue;
      if (!c || typeof c.amount !== 'number' || !Number.isFinite(c.amount))
        issues.push({ code: 'currency.amount', message: 'Enter an amount.' });
      if (!c || typeof c.currency !== 'string' || !/^[A-Z]{3}$/.test(c.currency))
        issues.push({ code: 'currency.code', message: 'Select a currency.' });
      else if (val.currencies && !val.currencies.includes(c.currency))
        issues.push({ code: 'currency.code', message: 'Currency not allowed.' });
      if (c && typeof c.amount === 'number' && val.min !== undefined && c.amount < val.min)
        issues.push({ code: 'min', message: `Must be at least ${val.min}.` });
      break;
    }
    case 'date':
      if (typeof v !== 'string' || !isValidDate(v)) issues.push({ code: 'date', message: 'Enter a valid date.' });
      else if (field.dateConstraint) {
        const today = new Date().toISOString().slice(0, 10);
        if (field.dateConstraint === 'past' && v > today) issues.push({ code: 'date.past', message: 'Date cannot be in the future.' });
        if (field.dateConstraint === 'future' && v < today) issues.push({ code: 'date.future', message: 'Date cannot be in the past.' });
      }
      break;
    case 'single_select':
    case 'multi_select': {
      const s = v as SelectValue;
      if (!s || !Array.isArray(s.selected)) {
        issues.push({ code: 'select', message: 'Invalid selection.' });
        break;
      }
      const opts = new Map((field.options ?? []).map((o) => [o.id, o]));
      for (const id of s.selected) {
        if (!opts.has(id)) issues.push({ code: 'select.unknown_option', message: 'Unknown option selected.' });
      }
      if (field.type === 'single_select' && s.selected.length > 1)
        issues.push({ code: 'select.single', message: 'Select only one option.' });
      const exclusive = s.selected.filter((id) => opts.get(id)?.exclusive);
      if (exclusive.length > 0 && s.selected.length > 1)
        issues.push({ code: 'select.exclusive', message: `“${opts.get(exclusive[0]!)?.label}” cannot be combined with other options.` });
      for (const id of s.selected) {
        const o = opts.get(id);
        if (o?.opensDetail && !(s.details?.[id] ?? '').trim())
          issues.push({ code: 'select.detail', message: `Please specify “${o.label}”.` });
        if ((s.details?.[id] ?? '').length > DEFAULT_LIMITS.shortTextHardMax)
          issues.push({ code: 'too_long', message: 'Detail is too long.' });
      }
      if (val.minItems && s.selected.length < val.minItems) issues.push({ code: 'select.min', message: `Select at least ${val.minItems}.` });
      if (val.maxItems && s.selected.length > val.maxItems) issues.push({ code: 'select.max', message: `Select at most ${val.maxItems}.` });
      break;
    }
    case 'yes_no':
      if (typeof v !== 'boolean') issues.push({ code: 'boolean', message: 'Choose yes or no.' });
      break;
    case 'file_upload': {
      const f = v as FileRefValue;
      if (!f || !Array.isArray(f.assetIds)) issues.push({ code: 'file', message: 'Invalid file reference.' });
      else if (val.maxFiles && f.assetIds.length > val.maxFiles) issues.push({ code: 'file.max', message: `At most ${val.maxFiles} files.` });
      break;
    }
    case 'asset_link': {
      const l = v as AssetLinkValue;
      if (!l || typeof l.url !== 'string' || !isSafeHttpUrl(l.url)) issues.push({ code: 'url', message: 'Enter a valid http(s) link.' });
      break;
    }
    case 'weekly_hours':
      issues.push(...validateWeeklyHours(v as WeeklyHoursValue));
      break;
    case 'contact_selector':
      if (typeof v !== 'string' || v.length === 0 || v.length > 64) issues.push({ code: 'contact', message: 'Select a contact.' });
      break;
    case 'acknowledgement':
      if (v !== true) issues.push({ code: 'ack', message: 'Acknowledgement is required.' });
      break;
    case 'signature': {
      const s = v as SignatureValue;
      if (!s || typeof s.name !== 'string' || s.name.trim().length < 2 || s.name.length > 200)
        issues.push({ code: 'signature', message: 'Type your full name to sign.' });
      break;
    }
    case 'repeater': {
      const r = v as RepeaterValue;
      if (!r || !Array.isArray(r.rows)) issues.push({ code: 'repeater', message: 'Invalid rows.' });
      else {
        const ids = new Set<string>();
        for (const row of r.rows) {
          if (!row.id || ids.has(row.id)) issues.push({ code: 'repeater.row_id', message: 'Duplicate or missing row id.' });
          ids.add(row.id);
        }
        const active = r.rows.filter((x) => !x.deletedAt).length;
        const max = Math.min(val.maxRows ?? DEFAULT_LIMITS.repeaterHardMaxRows, DEFAULT_LIMITS.repeaterHardMaxRows);
        if (active > max) issues.push({ code: 'repeater.max', message: `At most ${max} rows.` });
      }
      break;
    }
    case 'info':
      break;
  }
  return issues;
}

export function answerDisplay(field: FieldDef, a: AnswerValue | undefined): string {
  if (!a || a.kind === 'blank' || isBlankAnswer(a)) return '';
  if (a.kind === 'unknown') return 'Unknown / not sure';
  if (a.kind === 'na') return a.reason ? `Not applicable — ${a.reason}` : 'Not applicable';
  if (a.kind === 'waived') return 'Waived';
  const v = a.value as unknown;
  switch (field.type) {
    case 'yes_no':
      return v ? 'Yes' : 'No';
    case 'single_select':
    case 'multi_select': {
      const s = v as SelectValue;
      return s.selected
        .map((id) => {
          const o = field.options?.find((x) => x.id === id);
          const d = s.details?.[id];
          return o ? (d ? `${o.label}: ${d}` : o.label) : id;
        })
        .join(', ');
    }
    case 'currency': {
      const c = v as CurrencyValue;
      return `${c.currency} ${c.amount}`;
    }
    case 'asset_link':
      return (v as AssetLinkValue).url;
    case 'signature':
      return (v as SignatureValue).name;
    case 'acknowledgement':
      return v === true ? 'Acknowledged' : '';
    case 'file_upload':
      return `${(v as FileRefValue).assetIds.length} file(s)`;
    case 'repeater':
      return `${(v as RepeaterValue).rows.filter((r) => !r.deletedAt).length} row(s)`;
    case 'weekly_hours': {
      const h = v as WeeklyHoursValue;
      return WEEK_DAYS.map((d) => {
        const day = h.days[d];
        if (!day || day.closed) return `${d}: closed`;
        return `${d}: ${day.intervals.map((i) => `${i.open}–${i.close}`).join(', ')}`;
      }).join('; ');
    }
    default:
      return String(v);
  }
}
